<template>
  <div class="date-picker">
    <div :class="joinClasses('select', selectModifiers)">
      <div class="select__trigger" @click="toggleCalender">
        <input type="text" class="date-picker__native" ref="input" :value="isoDate(modelValue)" />

        <div class="select__label-wrapper">
          <div class="select__label" v-if="!hideIconAndLabel">
            {{ label }}
          </div>
        </div>

        <div class="select__arrow" v-if="!hideIconAndLabel">
          <UseSvg name="arrow_down_16" class="icon icon--small" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import flatpickr from 'flatpickr';
import I18n from 'i18n-js';

import { isoDate, formatFullDate, MONTHS } from '@/functions/date_format_helpers';
import joinClasses from '@/functions/join_classes';

import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  placeholder: { type: String, default: '' },
  modelValue: Date,
  minDate: { type: Date, default: null },
  selectModifiers: { type: Array, default: () => [] },
  hideIconAndLabel: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const label = computed(() => {
  return formatFullDate(props.modelValue) || props.placeholder;
});

let instance = null;
const input = ref(null);

onMounted(() => {
  // @ts-ignore
  instance = flatpickr(input.value, {
    monthSelectorType: 'static',
    locale: {
      weekdays: {
        shorthand: ["S", "M", "T", "W", "T", "F", "S"],
        longhand: ["S", "M", "T", "W", "T", "F", "S"]
      },
      months: {
        shorthand: MONTHS.map((month) => I18n.t(`months.shorthand.${month}`)),
        longhand: MONTHS.map((month) => I18n.t(`months.longhand.${month}`))
      }
    },
    prevArrow: `<button class="button button--small"><svg><use xlink:href="#arrow_left_16"></use></svg></button>`,
    nextArrow: `<button class="button button--small"><svg><use xlink:href="#arrow_right_16"></use></svg></button>`,
    onReady: (_selectedDates, _dateStr, instance) => {
      if (props.minDate) {
        instance.set('minDate', props.minDate);
      }

      if (instance.calendarContainer) {
        instance.calendarContainer.classList.add('date-picker__calendar');
      }
    },
    onChange: (selectedDates) => {
      emit('update:modelValue', selectedDates[0]);
    },
    onOpen: (_selectedDates, _dateStr, instance) => {
      if (props.minDate) {
        instance.set('minDate', props.minDate);
      }
    }
  });
});

function toggleCalender() {
  instance.toggle();
}
</script>

<style scoped lang="scss">
.date-picker {
  position: relative;

  &__native {
    width: 0;
    padding: 0;
    border: 0;
  }

  & .select {
    text-align: right;
  }
}
</style>

<style lang="scss">
@import 'flatpickr/dist/themes/dark';
@import '@/stylesheets/global';

$date-picker-width: rem(350);

// flatpickr calendar overrides
.date-picker__calendar {
  &.flatpickr-calendar {
    background: map-get($background-color, lighter);
    box-shadow: map-get($box-shadow, popover);
    overflow: hidden;
    border-radius: map-get($border-radius, mega);
    width: $date-picker-width;
  }

  .flatpickr-months {
    background: map-get($background-color, light);
    padding: bu(1.5);

    & .flatpickr-prev-month:hover svg, & .flatpickr-next-month:hover svg {
      fill: map-get($foreground-color, light);
    }

    .flatpickr-prev-month, .flatpickr-next-month {
      position: static;
      padding: 0;

      .button:not(:hover) {
        background-color: transparent;
      }

      svg {
        width: map-get($icon-size, small);
        height: map-get($icon-size, small);
      }
    }

    .flatpickr-month {
      background: map-get($background-color, light);

      .flatpickr-current-month {
        font-size: map-deep-get($typography, 'small-title', 'small', 'fs');
        color: map-get($foreground-color, light);

        .cur-month, .cur-year {
          font-weight: 500;
        }
      }
    }
  }

  .flatpickr-innerContainer {
    padding: bu(1) bu(3);
  }

  .flatpickr-weekday {
    background: map-get($background-color, lighter);
    font-weight: normal;
    color: map-get($foreground-color, dark);
    font-size: map-deep-get($typography, 'small-title', 'small', 'fs');
  }

  .flatpickr-day {
    color: map-get($foreground-color, light);
    font-size: map-deep-get($typography, 'small-title', 'small', 'fs');
    font-weight: 500;
    border: 0;
    @include transition(background-color);

    &.today {
      border: 0;
      background-color: map-get($background-color, lightest);
    }

    &.selected {
      border: 0;
      background-color: map-get($important-color, default);

      &:hover {
        background-color: map-get($important-color, default);
      }
    }

    &.prevMonthDay, &.nextMonthday {
      pointer-events: none;
      visibility: hidden !important;
    }

    &.flatpickr-disabled {
      color: map-get($background-color, lightest);

      &:hover {
        background-color: transparent;
      }
    }
  }

  .flatpickr-day.inRange, .flatpickr-day.prevMonthDay.inRange, .flatpickr-day.nextMonthDay.inRange, .flatpickr-day.today.inRange, .flatpickr-day.prevMonthDay.today.inRange, .flatpickr-day.nextMonthDay.today.inRange, .flatpickr-day:hover, .flatpickr-day.prevMonthDay:hover, .flatpickr-day.nextMonthDay:hover, .flatpickr-day:focus, .flatpickr-day.prevMonthDay:focus, .flatpickr-day.nextMonthDay:focus {
    border: 0;
    background-color: map-get($background-color, lightest);
  }

  .clear-date {
    background-color: map-get($background-color, light);
    padding: bu(1);
  }
}
</style>
